/**
 * Mana Pricing — API payloads and the shapes stored for borrower emails.
 *
 * One scenario carries two views of the same loan: `inputs` (the lender-sheet field
 * catalog, PRICING_FIELDS values) and `uwmInputs` (UWM's quote request body). Lenders
 * are sources: UWM prices live through its API, First Colony / PennyMac / Sun West price
 * on their rate sheets.
 */
import { z } from 'zod';
import { LenderRateRowSchema, LENDER_SCHEDULE_MAX_INTERVAL_MINUTES, LENDER_SCHEDULE_MIN_INTERVAL_MINUTES, loanTypesOf } from '../lender-pricing-fields.js';
import { PricingCompCategorySchema } from '../pricing-comp.js';

export const MANA_PRICING_SOURCES = ['uwm', 'fcm', 'pennymac', 'sunwest', 'amwest', 'tls', 'newrez', 'cardinal', 'springeq'] as const;
export type ManaPricingSource = (typeof MANA_PRICING_SOURCES)[number];
export const ManaPricingSourceSchema = z.enum(MANA_PRICING_SOURCES);
export const MANA_SOURCE_LABELS: Record<ManaPricingSource, string> = {
  uwm: 'UWM',
  fcm: 'First Colony',
  pennymac: 'PennyMac',
  sunwest: 'Sun West',
  amwest: 'AmWest',
  tls: 'Averra Financial',
  newrez: 'Newrez',
  cardinal: 'Cardinal Financial',
  springeq: 'Spring EQ',
};
export const SHEET_SOURCES: ManaPricingSource[] = ['fcm', 'pennymac', 'sunwest', 'amwest', 'tls', 'newrez', 'cardinal', 'springeq'];
/** Lenders that price home-equity scenarios (HELOC / second liens): UWM live, Spring EQ on its sheet. */
export const HOME_EQUITY_SOURCES: ManaPricingSource[] = ['uwm', 'springeq'];
/** Lenders that price first-lien mortgages. */
export const MORTGAGE_SOURCES: ManaPricingSource[] = ['uwm', 'fcm', 'pennymac', 'sunwest', 'amwest', 'tls', 'newrez', 'cardinal'];
/** Lenders that price DSCR (investor cash-flow) loans — the rate-sheet lenders; UWM has no DSCR product. */
export const DSCR_SOURCES: ManaPricingSource[] = ['fcm', 'pennymac', 'sunwest', 'amwest', 'newrez'];
/**
 * The sources a progressive quote asks for in their own requests ("lanes"): UWM and the engines
 * priced live on the lenders' portals, each of which can hang on its own. The sheet-only sources
 * answer at once and stay in the first answer. The page starts every lane together with the first
 * request and shows "<lender> pricing…" until that lane lands.
 */
export const MANA_LANE_SOURCES: ManaPricingSource[] = ['uwm', 'fcm', 'pennymac', 'sunwest', 'amwest', 'newrez'];
export const UWM_NO_DSCR_REASON = "UWM doesn't offer DSCR loans — DSCR prices on the rate-sheet lenders.";
/** A DSCR scenario (the Loan Type the shared inputs carry). */
/**
 * DSCR is the only loan type picked — UWM has nothing to price (it has no DSCR product). With
 * DSCR next to another loan type, UWM still prices the other one.
 */
export const isDscrInputs = (inputs: Record<string, unknown> | null | undefined): boolean => loanTypesOf(inputs).every((t) => /^dscr$/i.test(t));
/** DSCR is one of the picked loan types (the DSCR inputs apply to it). */
export const hasDscrLoanType = (inputs: Record<string, unknown> | null | undefined): boolean => loanTypesOf(inputs).some((t) => /^dscr$/i.test(t));
const MAX_LENDERS = MANA_PRICING_SOURCES.length;

export const ManaProductTypeSchema = z.enum(['mortgage', 'heloc']);
export type ManaProductType = z.infer<typeof ManaProductTypeSchema>;
/** 'grid' = the Term × Program best-rates email; 'options' = the shortlisted options table; 'realtor' = the Realtor Marketing rate table. */
export const ManaEmailFormatSchema = z.enum(['grid', 'options', 'realtor']);
/** The connection a scenario email goes out through: the MLO's Gmail (SMTP app password) or their SendGrid API key — both saved under Integrations. */
export const ManaEmailTransportSchema = z.enum(['gmail', 'sendgrid']);
export type ManaEmailTransport = z.infer<typeof ManaEmailTransportSchema>;
export const MANA_EMAIL_TRANSPORT_LABELS: Record<ManaEmailTransport, string> = { gmail: 'Gmail', sendgrid: 'SendGrid' };
export type ManaEmailFormat = z.infer<typeof ManaEmailFormatSchema>;
/** The best-rates email's template (its subject, headline and intro); absent = derived from the scenario. */
export const ManaEmailTemplateSchema = z.enum(['purchase', 'refinance', 'cash_out', 'dscr', 'home_equity']);
const RealtorPickSchema = z.object({ optionId: z.string().min(1).max(240), rate: z.number().min(0).max(30) });
const RealtorColumnPicksSchema = z.object({ par: RealtorPickSchema.optional(), onePoint: RealtorPickSchema.optional(), twoPoints: RealtorPickSchema.optional() });
/** Rates the MLO pinned for the Realtor Marketing table, per program row × price column. */
export const ManaRealtorPicksSchema = z.object({ arm_5_6_30: RealtorColumnPicksSchema.optional(), fixed_30: RealtorColumnPicksSchema.optional(), lp_2_1_30: RealtorColumnPicksSchema.optional(), fha_fixed_30: RealtorColumnPicksSchema.optional(), fha_arm_5_1_30: RealtorColumnPicksSchema.optional() });
/** One or several recipients, comma / semicolon separated (the realtor blast). */
export const ManaRecipientsSchema = z
  .string()
  .trim()
  .min(3)
  .max(800)
  .refine((s) => s.split(/[,;\s]+/).filter(Boolean).every((e) => z.string().email().safeParse(e).success), { message: 'Enter one or more valid email addresses, separated by commas.' })
  .refine((s) => s.split(/[,;\s]+/).filter(Boolean).length <= 25, { message: 'At most 25 recipients per send.' });
export const recipientsOf = (s: string): string[] => [...new Set(s.split(/[,;\s]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];

export const MANA_PROGRAM_KEYS = ['fixed', '1-0bp', '1-1bp', '2-1bp', '3-2-1bp', '1-0llpa', '1-1llpa', '2-1llpa', '3-2-1llpa'] as const;
/** Program columns are derived from the scenario's Temporary Buydown selection at send time; stored keys are a fallback. */
const ProgramKeysSchema = z.array(z.enum(MANA_PROGRAM_KEYS)).max(MANA_PROGRAM_KEYS.length).default([]);
const CypBpsSchema = z.number().int().min(-1000).max(1000).default(0);
const BorrowerNameSchema = z.string().trim().min(1).max(200);
const OptionalEmail = z.string().email().optional().or(z.literal(''));
const OptionalPhone = z.string().max(40).optional().or(z.literal(''));
/** Option ids picked for the borrower (sheet option ids, or `uwm:` ids for UWM products). */
export const ManaSelectionSchema = z.array(z.string().min(1).max(240)).max(25);

/** LOAN PARAMETERS block of the grid email (resolved labels). */
export const ManaGridParamsSchema = z.object({
  loanAmount: z.string().max(40),
  appraisedValue: z.string().max(40),
  creditScore: z.string().max(10),
  state: z.string().max(20),
  occupancy: z.string().max(60),
  loanPurpose: z.string().max(60),
  /** "DSCR", "Conventional / FHA" … — sends always use the scenario's current picks. */
  loanType: z.string().max(60).optional(),
  /** "Yes" / "No" — sends always use the scenario's current value. */
  waiveEscrows: z.string().max(10).optional(),
});
export type ManaGridParams = z.infer<typeof ManaGridParamsSchema>;

const ScenarioCore = {
  borrowerName: BorrowerNameSchema,
  email: OptionalEmail,
  phone: OptionalPhone,
  productType: ManaProductTypeSchema,
  inputs: z.record(z.unknown()),
  uwmInputs: z.record(z.unknown()).nullable().optional(),
  lenders: z.array(ManaPricingSourceSchema).max(MAX_LENDERS),
  cypBps: CypBpsSchema,
  /** UWM's ★ category, resolved like the Pricing module (purpose id + first loan type). */
  uwmCategory: PricingCompCategorySchema.nullable().optional(),
  /** The best-rates email template the MLO chose for this scenario (null = the scenario's default). */
  emailTemplate: ManaEmailTemplateSchema.nullable().optional(),
  /** Rates pinned for the Realtor Marketing table. */
  realtorPicks: ManaRealtorPicksSchema.nullable().optional(),
};

/**
 * POST /api/mana-pricing/quote — saves the scenario (before pricing), then prices every picked source.
 * Progressive quoting: "fast" prices every picked source except the lane sources (UWM and the live
 * engines) and names those as `pending`; "slow" prices only the `only` sources named (nothing is
 * saved again) — the page sends one such request per pending source, so a hanging engine delays
 * only its own answer. "slow" without `only` = the legacy PennyMac step. Absent → everything in one answer.
 */
export const ManaQuoteRequestSchema = z.object({
  ...ScenarioCore,
  lenders: z.array(ManaPricingSourceSchema).min(1).max(MAX_LENDERS),
  selection: ManaSelectionSchema.optional(),
  phase: z.enum(['fast', 'slow']).optional(),
  /** "fast" only: the page will ask for every lane source itself (a page from before 2026-10-17 doesn't, so its "fast" step prices everything but PennyMac). */
  lanes: z.boolean().optional(),
  /** "slow" only: the picked sources this request prices (one lane). */
  only: z.array(ManaPricingSourceSchema).min(1).max(MAX_LENDERS).optional(),
});
export type ManaQuoteRequest = z.infer<typeof ManaQuoteRequestSchema>;

/** POST /api/mana-pricing/scenarios — save without pricing (also the tray auto-save). */
export const ManaScenarioSaveSchema = z.object({
  ...ScenarioCore,
  rates: z.array(LenderRateRowSchema).max(25).optional(),
  selection: ManaSelectionSchema.optional(),
});
export type ManaScenarioSave = z.infer<typeof ManaScenarioSaveSchema>;

/** POST /api/mana-pricing/email/grid — the best rate across the picked lenders for each term × program. */
export const ManaGridEmailSchema = z.object({
  to: z.string().email(),
  borrowerName: BorrowerNameSchema,
  productType: ManaProductTypeSchema,
  inputs: z.record(z.unknown()),
  uwmInputs: z.record(z.unknown()).nullable().optional(),
  lenders: z.array(ManaPricingSourceSchema).min(1).max(MAX_LENDERS),
  programKeys: ProgramKeysSchema,
  params: ManaGridParamsSchema,
  cypBps: CypBpsSchema,
  uwmCategory: PricingCompCategorySchema.nullable().optional(),
  template: ManaEmailTemplateSchema.nullable().optional(),
  /** Gmail (default) or SendGrid. */
  transport: ManaEmailTransportSchema.optional(),
});
export type ManaGridEmailInput = z.infer<typeof ManaGridEmailSchema>;

/** POST /api/mana-pricing/email/realtor — the Realtor Marketing rate table, to one or more realtors. */
const RealtorCandidateSchema = z.object({
  source: ManaPricingSourceSchema,
  lender: z.string().max(120),
  product: z.string().max(200),
  rate: z.number(),
  price: z.number(),
  credit: z.number(),
  asOf: z.string().nullable(),
});
/** One cell of the Realtor Marketing table as the results screen built it (Email now / Preview send it as shown). */
export const ManaRealtorCellSchema = z.object({
  row: z.enum(['arm_5_6_30', 'fixed_30', 'lp_2_1_30', 'fha_fixed_30', 'fha_arm_5_1_30']),
  column: z.enum(['par', 'onePoint', 'twoPoints']),
  winner: RealtorCandidateSchema.extend({ optionId: z.string().max(200), apr: z.number(), pi: z.number(), pinned: z.boolean(), star: z.boolean(), parPrice: z.number() }).nullable(),
  candidates: z.array(RealtorCandidateSchema).max(80),
});
/** US state codes whose realtor contacts (the sender's own list) receive the email. */
export const ManaRealtorStatesSchema = z.array(z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/)).max(60);
export const ManaRealtorEmailSchema = z.object({
  /** Extra addresses typed by hand (comma-separated); the realtor list itself comes from `realtorStates`. */
  to: ManaRecipientsSchema.or(z.literal('')).optional(),
  realtorStates: ManaRealtorStatesSchema.optional(),
  /** "Hi Sarah," — optional. */
  recipientName: z.string().trim().max(80).optional().or(z.literal('')),
  borrowerName: BorrowerNameSchema,
  productType: ManaProductTypeSchema,
  inputs: z.record(z.unknown()),
  uwmInputs: z.record(z.unknown()).nullable().optional(),
  lenders: z.array(ManaPricingSourceSchema).min(1).max(MAX_LENDERS),
  params: ManaGridParamsSchema,
  cypBps: CypBpsSchema,
  uwmCategory: PricingCompCategorySchema.nullable().optional(),
  realtorPicks: ManaRealtorPicksSchema.nullable().optional(),
  /** The table as shown on the results screen (when the scenario already prices all five programs): sent as is, no re-pricing. Absent → priced live. */
  cells: z.array(ManaRealtorCellSchema).max(15).optional(), // five rows × three columns
  /** When those results were priced (ISO). */
  ratesAsOf: z.string().datetime({ offset: true }).optional(),
  /** Gmail (default) or SendGrid. */
  transport: ManaEmailTransportSchema.optional(),
});
export type ManaRealtorEmailInput = z.infer<typeof ManaRealtorEmailSchema>;

/** POST /api/mana-pricing/email/options — the shortlisted options, as shown. */
export const ManaOptionsEmailSchema = z.object({
  to: z.string().email(),
  borrowerName: BorrowerNameSchema,
  inputs: z.record(z.unknown()),
  rates: z.array(LenderRateRowSchema).min(1).max(25),
  ratesAsOf: z.string().datetime().nullable().optional(),
  /** Gmail (default) or SendGrid. */
  transport: ManaEmailTransportSchema.optional(),
});
export type ManaOptionsEmailInput = z.infer<typeof ManaOptionsEmailSchema>;

export const MANA_SCHEDULE_MIN_INTERVAL_MINUTES = LENDER_SCHEDULE_MIN_INTERVAL_MINUTES;

/** POST /api/mana-pricing/schedules — one schedule per borrower per email format. */
export const ManaScheduleCreateSchema = z.object({
  borrowerName: BorrowerNameSchema,
  /** One address; the realtor format takes extra comma-separated addresses (may be blank when `realtorStates` is set). */
  toEmail: ManaRecipientsSchema.or(z.literal('')),
  /** realtor: the states whose contacts get every send (resolved at send time, opt-outs excluded). */
  realtorStates: ManaRealtorStatesSchema.optional(),
  format: ManaEmailFormatSchema,
  /** grid: the template (null = the scenario's default). */
  template: ManaEmailTemplateSchema.nullable().optional(),
  /** realtor: "Hi Sarah," */
  recipientName: z.string().trim().max(80).optional().or(z.literal('')),
  /** realtor: the rates pinned on the results screen (also kept on the saved scenario). */
  realtorPicks: ManaRealtorPicksSchema.nullable().optional(),
  productType: ManaProductTypeSchema,
  inputs: z.record(z.unknown()),
  uwmInputs: z.record(z.unknown()).nullable().optional(),
  lenders: z.array(ManaPricingSourceSchema).min(1).max(MAX_LENDERS),
  /** grid: the program columns (derived from the scenario's Temporary Buydown; stored as a fallback / for display). */
  programKeys: ProgramKeysSchema,
  /** options: the picked option ids; empty = every eligible option. */
  productIds: ManaSelectionSchema.default([]),
  params: ManaGridParamsSchema.nullable().optional(),
  cypBps: CypBpsSchema,
  uwmCategory: PricingCompCategorySchema.nullable().optional(),
  intervalMinutes: z.number().int().min(LENDER_SCHEDULE_MIN_INTERVAL_MINUTES).max(LENDER_SCHEDULE_MAX_INTERVAL_MINUTES),
  startAt: z.string().datetime().nullable().optional(),
  /** Every send goes out through this connection: Gmail (default) or SendGrid. */
  transport: ManaEmailTransportSchema.optional(),
});
export type ManaScheduleCreate = z.infer<typeof ManaScheduleCreateSchema>;

export const ManaSchedulePatchSchema = z
  .object({
    active: z.boolean().optional(),
    startAt: z.string().datetime().nullable().optional(),
  })
  .refine((b) => b.active !== undefined || b.startAt !== undefined, { message: 'Provide active and/or startAt.' });

// ---------------------------------------------------------------------------
// What was sent (kept for the MLO — the borrower never sees lender names)
// ---------------------------------------------------------------------------

export interface ManaRateCandidate {
  source: ManaPricingSource;
  lender: string;
  product: string;
  rate: number;
  /** 100-based price at that rate (after comp as each lender prices it). */
  price: number;
  /** Lender credit in dollars (+) or cost (−) at that rate. */
  credit: number;
  /** When this rate was priced: UWM = the live quote time, rate sheets = the sheet's publish time. */
  asOf: string | null;
}

export interface ManaGridCell {
  termYears: number;
  /** "Conventional Fixed", "FHA ARM", … when the scenario priced several loan types / amortizations. */
  kind?: string;
  programKey: string;
  program: string;
  winner: ManaRateCandidate | null;
  /** Each lender's ★ rate for this cell (the lowest qualifying rate per product). */
  candidates: ManaRateCandidate[];
  /** Monthly MI shown in the email (UWM's estimate) and where it came from. */
  mi: number | null;
  miFrom: string | null;
}

export interface ManaOptionsSentRow {
  lender: string;
  product: string;
  rate: string;
  points: string;
  lenderCredit: string;
  monthlyPayment: string;
  asOf: string | null;
  productId?: string;
}

export interface ManaSendLogDto {
  id: string;
  scheduleId: string | null;
  borrowerName: string;
  toEmail: string;
  format: ManaEmailFormat;
  kind: 'email' | 'schedule' | 'preview' | 'test';
  status: string;
  error: string | null;
  notes: string[];
  cells: ManaGridCell[];
  rows: ManaOptionsSentRow[];
  createdAt: string;
}
